import React, { useState, useEffect } from 'react';
import { supabase } from '../supabaseClient';
import { useData } from '../DataContext';
import './Pokes.css';

const CONTACT_ICONS = {
  discord: '💬',
  instagram: '📷',
  email: '📧',
  snapchat: '👻',
  phone: '📞',
  twitter: '🦋',
  telegram: '✈️',
  default: '💌'
};

function detectContactType(contact) {
  if (!contact) return 'default';
  const lower = contact.toLowerCase();
  if (lower.includes('discord') || lower.includes('#')) return 'discord';
  if (lower.includes('instagram') || (lower.includes('@') && !lower.includes('.'))) return 'instagram';
  if ((lower.includes('@')) && (lower.includes('.'))) return 'email';
  if (lower.includes('snap')) return 'snapchat';
  if (lower.match(/\d{3}[-.]?\d{3}[-.]?\d{4}/)) return 'phone';
  if (lower.includes('twitter') || lower.includes('x.com')) return 'twitter';
  if (lower.includes('telegram') || lower.includes('t.me')) return 'telegram';
  return 'default';
}

export default function Pokes({ session }) {
  const { getPokes } = useData();
  const [loading, setLoading] = useState(true);
  const [receivedPokes, setReceivedPokes] = useState([]);
  const [sentPokes, setSentPokes] = useState([]);
  const [processing, setProcessing] = useState(null);

  useEffect(() => {
    const loadPokes = async () => {
      try {
        const { received, sent } = await getPokes(session.user.email);
        setReceivedPokes(received);
        setSentPokes(sent);
      } catch (error) {
        console.error('Error loading pokes:', error);
      } finally {
        setLoading(false);
      }
    };

    loadPokes();
  }, [session.user.email, getPokes]);

  const handleAccept = async (fromUser) => {
    const prev = receivedPokes;
    try {
      setProcessing(fromUser);
      
      // Optimistic update
      setReceivedPokes(prevPokes => 
        prevPokes.map(p => p.from_user === fromUser ? { ...p, accepted: true, seen: true } : p)
      );

      const { error } = await supabase
        .from('pokes')
        .update({ accepted: true, seen: true })
        .eq('from_user', fromUser)
        .eq('to_user', session.user.email);

      if (error) throw error;

      // Reload to get fresh contact data for both users
      const { received } = await getPokes(session.user.email, true);
      setReceivedPokes(received);
    } catch (error) {
      console.error('Error accepting poke:', error);
      setReceivedPokes(prev);
    } finally {
      setProcessing(null);
    }
  };

  const handleReject = async (fromUser) => {
    const prev = receivedPokes;
    try {
      setProcessing(fromUser);
      setReceivedPokes(prevPokes => 
        prevPokes.map(p => p.from_user === fromUser ? { ...p, seen: true } : p)
      );

      const { error } = await supabase
        .from('pokes')
        .update({ seen: true })
        .eq('from_user', fromUser)
        .eq('to_user', session.user.email);

      if (error) throw error;

      const { received } = await getPokes(session.user.email, true);
      setReceivedPokes(received);
    } catch (error) {
      console.error('Error rejecting poke:', error);
      setReceivedPokes(prev);
    } finally {
      setProcessing(null);
    }
  };

  if (loading) {
    return <div className="loading">loading pokes...</div>;
  }

  const unseenPokes = receivedPokes.filter(p => !p.seen);
  const seenPokes = receivedPokes.filter(p => p.seen);

  return (
    <div className="pokes-page">
      <div className="pokes-container">
        <h1>pokes</h1>

        {unseenPokes.length > 0 && (
          <div className="pokes-section">
            <h2>new pokes ({unseenPokes.length})</h2>
            {unseenPokes.map(poke => (
              <div key={poke.from_user} className="poke-card new">
                <div 
                  className="avatar" 
                  style={{ 
                    backgroundImage: poke.user?.profile_image 
                      ? `url(${poke.user.profile_image})` 
                      : `url(/img/default.png)`,
                    backgroundSize: 'cover',
                    backgroundPosition: 'center',
                    backgroundRepeat: 'no-repeat'
                  }}
                />
                <div className="poke-content">
                  <div className="poke-header">
                    <span className="username">{poke.user?.username || 'unknown'}</span>
                    <span className="poke-label">wants to connect</span>
                  </div>
                  {poke.user?.bio && <p className="bio">{poke.user.bio}</p>}
                  <div className="poke-actions">
                    <button 
                      className="accept-btn"
                      onClick={() => handleAccept(poke.from_user)}
                      disabled={processing === poke.from_user}
                    >
                      {processing === poke.from_user ? 'processing...' : 'accept & share contact'}
                    </button>
                    <button 
                      className="reject-btn"
                      onClick={() => handleReject(poke.from_user)}
                      disabled={processing === poke.from_user}
                    >
                      {processing === poke.from_user ? 'processing...' : 'ignore'}
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {seenPokes.length > 0 && (
          <div className="pokes-section">
            <h2>previous</h2>
            {seenPokes.map(poke => (
              <div key={poke.from_user} className="poke-card">
                <div 
                  className="avatar" 
                  style={{ 
                    backgroundImage: poke.user?.profile_image 
                      ? `url(${poke.user.profile_image})` 
                      : `url(/img/default.jpg)`,
                    backgroundSize: 'cover',
                    backgroundPosition: 'center'
                  }}
                />
                <div className="poke-content">
                  <div className="poke-header">
                    <span className="username">{poke.user?.username || 'unknown'}</span>
                    {poke.accepted ? (
                      <span className="status accepted">✓ accepted</span>
                    ) : (
                      <span className="status ignored">ignored</span>
                    )}
                  </div>
                  {poke.accepted && poke.user?.contact_info && (
                    <div className="contact-info">
                      <span className="contact-icon">
                        {CONTACT_ICONS[detectContactType(poke.user.contact_info)]}
                      </span>
                      <span className="contact-text">{poke.user.contact_info}</span>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}

        {sentPokes.length > 0 && (
          <div className="pokes-section">
            <h2>sent pokes</h2>
            {sentPokes.map(poke => (
              <div key={poke.to_user} className="poke-card sent">
                <div 
                  className="avatar" 
                  style={{ 
                    backgroundImage: poke.user?.profile_image 
                      ? `url(${poke.user.profile_image})` 
                      : `url(/img/default.jpg)`,
                    backgroundSize: 'cover',
                    backgroundPosition: 'center'
                  }}
                />
                <div className="poke-content">
                  <div className="poke-header">
                    <span className="username">{poke.user?.username || 'unknown'}</span>
                    {poke.accepted ? (
                      <span className="status accepted">✓ accepted</span>
                    ) : (
                      <span className="status pending">pending</span>
                    )}
                  </div>
                  {poke.accepted && poke.user?.contact_info && (
                    <div className="contact-info">
                      <span className="contact-icon">
                        {CONTACT_ICONS[detectContactType(poke.user.contact_info)]}
                      </span>
                      <span className="contact-text">{poke.user.contact_info}</span>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}

        {receivedPokes.length === 0 && sentPokes.length === 0 && (
          <div className="empty-state">
            <p>no pokes yet</p>
          </div>
        )}
      </div>
    </div>
  );
}